import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { type MouseEvent, type ReactElement, useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from '@/shared/lib';
import { cn } from '@/shared/ui';
import { HeroCursor } from './hero-cursor';
import { HeroSlide } from './hero-slide';
import type { HeroSlideItem } from './hero-slide-item';
import { HeroSliderControls } from './hero-slider-controls';

/** 자동 넘김 간격(ms). */
const AUTOPLAY_DELAY_MS = 6000;

interface CursorState {
  readonly x: number;
  readonly y: number;
  readonly side: 'prev' | 'next';
}

/** 마우스처럼 정밀한 포인터인지 — 터치에서 따라오는 마우스 흉내 이벤트로 원이 남지 않게 한다. */
const hasFinePointer = (): boolean =>
  typeof window.matchMedia !== 'function' ||
  window.matchMedia('(hover: hover) and (pointer: fine)').matches;

/** 누른 곳과 뗀 곳이 이만큼(px) 넘게 다르면 끌어서 넘긴 것으로 보고 클릭으로 치지 않는다. */
const DRAG_THRESHOLD_PX = 6;

/** 링크·버튼 위에서는 따라다니는 원을 숨기고 원래 동작(상세 이동·조작)을 그대로 둔다. */
const isOverControl = (target: EventTarget): boolean =>
  target instanceof Element && target.closest('a, button') !== null;

interface HeroSliderProps {
  /** 슬라이드 순서 = 배너 순서(배너가 없으면 추천 순서). 한 장이면 넘기지 않는다. */
  readonly slides: readonly HeroSlideItem[];
}

/**
 * HOME 히어로 슬라이더 — 배너마다 한 장, 가로로 밀어 넘긴다(끌기·이전/다음·6초 자동 넘김).
 * 정지 버튼을 두고(KWCAG 자동 재생 멈춤 수단), 동작 줄이기 설정이면 자동으로 넘기지 않고 정지 버튼도 내지 않는다.
 * 맨 아래 진행 막대는 지금 장의 칸만 칠한다.
 * 마우스를 올리면 둥근 ‹ 현재/전체 › 표시가 커서를 따라다니고, 화면 왼쪽 절반을 누르면 이전·오른쪽 절반을
 * 누르면 다음 장으로 넘긴다(끌어서 넘긴 직후의 클릭은 무시). 키보드·터치는 아래쪽 버튼과 끌기로 넘긴다.
 */
export const HeroSlider = ({ slides }: HeroSliderProps): ReactElement => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const canSlide = slides.length > 1;
  const [autoplay] = useState(() =>
    Autoplay({ delay: AUTOPLAY_DELAY_MS, playOnInit: false, stopOnInteraction: false }),
  );
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: canSlide, duration: 38 }, [autoplay]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [cursor, setCursor] = useState<CursorState | null>(null);
  const pressX = useRef<number | null>(null);
  const canAutoplay = canSlide && !prefersReducedMotion;
  const shouldAutoplay = canAutoplay && isPlaying;

  useEffect(() => {
    if (emblaApi === undefined) return undefined;
    const syncSelected = (): void => setSelectedIndex(emblaApi.selectedScrollSnap());
    syncSelected();
    emblaApi.on('select', syncSelected).on('reInit', syncSelected);
    return () => {
      emblaApi.off('select', syncSelected).off('reInit', syncSelected);
    };
  }, [emblaApi]);

  useEffect(() => {
    const plugin = emblaApi?.plugins().autoplay;
    if (plugin === undefined) return;
    if (shouldAutoplay) {
      plugin.play();
    } else {
      plugin.stop();
    }
  }, [emblaApi, shouldAutoplay]);

  const trackCursor = (event: MouseEvent<HTMLDivElement>): void => {
    if (!canSlide || !hasFinePointer() || isOverControl(event.target)) {
      setCursor(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    setCursor({ x, y: event.clientY - rect.top, side: x < rect.width / 2 ? 'prev' : 'next' });
  };

  const slideByClick = (event: MouseEvent<HTMLDivElement>): void => {
    const draggedBy = pressX.current === null ? 0 : Math.abs(event.clientX - pressX.current);
    if (!canSlide || isOverControl(event.target) || draggedBy > DRAG_THRESHOLD_PX) return;
    const rect = event.currentTarget.getBoundingClientRect();
    if (event.clientX - rect.left < rect.width / 2) {
      emblaApi?.scrollPrev();
    } else {
      emblaApi?.scrollNext();
    }
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: 마우스 전용 보조 조작 — 같은 동작을 아래쪽 이전·다음 버튼이 키보드로 제공한다.
    // biome-ignore lint/a11y/useKeyWithClickEvents: 위와 같은 이유로 키보드 조작은 이전·다음 버튼이 맡는다.
    <div
      className={cn('absolute inset-0', cursor !== null && 'cursor-none')}
      onMouseMove={trackCursor}
      onMouseLeave={() => setCursor(null)}
      onMouseDown={(event) => {
        pressX.current = event.clientX;
      }}
      onClick={slideByClick}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: fieldset은 캐러셀 뷰포트에 맞지 않는다. */}
      <div
        ref={emblaRef}
        role="group"
        aria-roledescription="carousel"
        aria-label="추천 상품"
        className="size-full overflow-hidden"
      >
        <ul className="flex h-full touch-pan-y touch-pinch-zoom">
          {slides.map((slide, index) => (
            <li
              key={slide.id}
              aria-roledescription="slide"
              aria-label={`${index + 1} / ${slides.length}`}
              // 화면 밖 장의 보기 링크로 초점이 들어가지 않게 한다.
              inert={canSlide && index !== selectedIndex}
              className="h-full min-w-0 shrink-0 grow-0 basis-full"
            >
              <HeroSlide copy={slide.copy} imageUrl={slide.imageUrl} isFirst={index === 0} />
            </li>
          ))}
        </ul>
      </div>

      {cursor !== null ? (
        <HeroCursor
          x={cursor.x}
          y={cursor.y}
          side={cursor.side}
          selectedIndex={selectedIndex}
          count={slides.length}
        />
      ) : null}

      {canSlide ? (
        <>
          {/* 마우스가 있는 화면에서는 따라다니는 원으로 넘기므로 숨기고, 키보드로 들어오면(focus-within) 드러낸다.
              터치 화면에서는 늘 보인다 — 자동 넘김 정지 수단이 여기에 있다. */}
          <div className="pointer-events-none absolute inset-x-0 bottom-8 z-20 px-5 transition-opacity focus-within:opacity-100 sm:bottom-12 sm:px-8 xl:px-14 [@media(hover:hover)_and_(pointer:fine)]:opacity-0 [@media(hover:hover)_and_(pointer:fine)]:focus-within:opacity-100">
            <div className="pointer-events-auto inline-flex">
              <HeroSliderControls
                selectedIndex={selectedIndex}
                count={slides.length}
                onPrev={() => emblaApi?.scrollPrev()}
                onNext={() => emblaApi?.scrollNext()}
                canAutoplay={canAutoplay}
                isPlaying={isPlaying}
                onTogglePlay={() => setIsPlaying((playing) => !playing)}
              />
            </div>
          </div>

          {/* 진행 막대 — 전체를 장 수로 나눈 칸 중 지금 장의 칸만 칠한다. */}
          <div aria-hidden className="absolute inset-x-0 bottom-0 z-20 h-1 bg-background/40">
            <div
              className="h-full bg-primary transition-[margin] duration-500 ease-out motion-reduce:transition-none"
              style={{
                width: `${100 / slides.length}%`,
                marginLeft: `${(100 / slides.length) * selectedIndex}%`,
              }}
            />
          </div>
        </>
      ) : null}
    </div>
  );
};
